import { useState } from "react";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, MonoMeta, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

// Invite-accept screen. Visual parity with B_Invite in
// design_handoff_sytofood/screens-b2.jsx (~line 358-412): logo, mono
// eyebrow with the invite code, editorial display heading, surface card
// summarising the household, tangerine CTA + ghost "later" button.
// The actual household name comes from the server only after acceptance,
// so we keep the heading generic ("Witamy w household") instead of
// hard-coding a name.

type Props = {
  code: string;
  onDone: () => void;
};

export function InviteAcceptScreen({ code, onDone }: Props) {
  const { refresh } = useAuth();
  const { lang } = useLang();
  const t = authCopy[lang];
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setError(null);
    setSubmitting(true);
    try {
      await api.acceptInvite(code);
      await refresh();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.iaJoinError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 20 }}>
        <SytoLogo size={22} />
      </div>

      <div style={{ marginTop: 32 }}>
        <Eyebrow>ZAPROSZENIE · /invite/{code}</Eyebrow>
        <h1
          style={{
            marginTop: 14,
            fontFamily: "var(--font-display)",
            fontSize: 36,
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1.05,
            color: "var(--ink)",
          }}
        >
          {t.iaHeadingLine1}
          <br />
          {t.iaHeadingLine2}
          <span style={{ color: "var(--accent)" }}>.</span>
        </h1>
        <p
          style={{
            marginTop: 14,
            fontSize: 14,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          {t.iaIntro}
        </p>
      </div>

      <div
        style={{
          marginTop: 28,
          padding: 18,
          borderRadius: 14,
          background: "var(--surface)",
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <MonoMeta>{t.iaCodeLabel}</MonoMeta>
        <div
          style={{
            marginTop: 4,
            fontFamily: "var(--font-mono)",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "var(--ink)",
          }}
        >
          {code}
        </div>
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--nav-border)",
            fontSize: 12,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          {t.iaCardBody}
        </div>
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 14 }}
        >
          {error}
        </p>
      ) : null}

      <div
        style={{
          marginTop: "auto",
          paddingTop: 32,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <PrimaryButton onClick={handleAccept} disabled={submitting}>
          {submitting ? t.iaSubmitting : t.iaSubmit}
        </PrimaryButton>
        <button
          type="button"
          onClick={onDone}
          disabled={submitting}
          style={{
            padding: 14,
            background: "transparent",
            color: "var(--ink-2)",
            fontSize: 13,
            fontWeight: 600,
            border: "none",
          }}
        >
          {t.iaLater}
        </button>
      </div>
    </ScreenShell>
  );
}
